/* =========================================================================
   CASCADE LIGHTING — global enhancements (deferred, no dependencies)
   1. Header .is-scrolled state (Dawn's <sticky-header> is left untouched)
   2. Back-to-top button
   3. Footer accordion (<cascade-footer-accordion>, mobile only)
   4. Mobile menu drawer: the "Shop" submenu opens in place as an accordion
      instead of Dawn's sliding panel (Dawn's MenuDrawer JS is unchanged;
      only clicks on .cascade-accordion summaries are handled here).
   5. Desktop header menus (dropdown + mega) open on mouse hover as well as click
   ========================================================================= */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* 1 + 2. Scroll: header state and back-to-top ----------------------------- */
  let ticking = false;

  const onScroll = () => {
    const y = window.scrollY;
    // Queried on every frame so it keeps working after the theme editor re-renders the header
    const header = document.querySelector('.cascade-header-wrapper');
    if (header) header.classList.toggle('is-scrolled', y > 40);

    const backToTop = document.querySelector('[data-cl-back-to-top]');
    if (backToTop) {
      const show = y > window.innerHeight * 1.2;
      if (show && backToTop.hidden) {
        backToTop.hidden = false;
        requestAnimationFrame(() => backToTop.classList.add('is-visible'));
      } else if (!show && !backToTop.hidden) {
        backToTop.classList.remove('is-visible');
        backToTop.hidden = true;
      }
    }
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(onScroll);
    },
    { passive: true }
  );
  onScroll();

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-cl-back-to-top]')) return;
    window.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    document.querySelector('.header__heading-link')?.focus({ preventScroll: true });
  });

  /* 3. Footer accordion ------------------------------------------------------ */
  class CascadeFooterAccordion extends HTMLElement {
    constructor() {
      super();
      this.desktop = window.matchMedia('(min-width: 768px)');
      this.onToggle = this.onToggle.bind(this);
      this.setMode = this.setMode.bind(this);
    }

    connectedCallback() {
      this.button = this.querySelector('[data-footer-toggle]');
      if (!this.button) return;
      this.button.addEventListener('click', this.onToggle);
      this.desktop.addEventListener('change', this.setMode);
      this.setMode();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onToggle);
      this.desktop.removeEventListener('change', this.setMode);
    }

    setMode() {
      if (this.desktop.matches) {
        this.setOpen(true);
        this.button.setAttribute('tabindex', '-1');
      } else {
        this.setOpen(false);
        this.button.removeAttribute('tabindex');
      }
    }

    setOpen(open) {
      this.classList.toggle('is-open', open);
      this.button.setAttribute('aria-expanded', String(open));
    }

    onToggle() {
      if (this.desktop.matches) return;
      this.setOpen(!this.classList.contains('is-open'));
    }
  }

  if (!customElements.get('cascade-footer-accordion')) {
    customElements.define('cascade-footer-accordion', CascadeFooterAccordion);
  }

  /* 4. Menu drawer accordion -------------------------------------------------- */
  // Capture phase on document runs before Dawn's listeners on the <summary>, so
  // Dawn's slide-in + focus trap is skipped for these items. The native
  // <details> toggle still happens; Escape keeps using Dawn's closeSubmenu().
  document.addEventListener(
    'click',
    (event) => {
      const summary = event.target.closest('.cascade-accordion > summary');
      if (!summary) return;
      event.stopPropagation();
      const details = summary.parentElement;
      const willOpen = !details.hasAttribute('open');
      summary.setAttribute('aria-expanded', String(willOpen));
      details.classList.toggle('menu-opening', willOpen);
    },
    true
  );

  /* 5. Desktop header menus open on hover ------------------------------------- */
  // Dawn's <header-menu> (dropdown and mega menu) only opens on click. With a
  // mouse on desktop it now also opens on hover; the mega menu CSS already has a
  // hover bridge over the gap. Keyboard and touch keep Dawn's click behaviour.
  // Delegated on document so it survives the theme editor re-rendering the header.
  const hoverMenus = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
  const HOVER_CLOSE_DELAY = 200;

  const setMenuOpen = (menu, open) => {
    const details = menu.querySelector(':scope > details');
    if (!details || details.open === open) return;
    details.open = open;
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(open));
  };

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse' || !hoverMenus.matches) return;
    const menu = event.target.closest('header-menu');
    if (!menu) return;
    clearTimeout(menu.cascadeCloseTimer);
    document.querySelectorAll('header-menu').forEach((other) => {
      if (other !== menu) setMenuOpen(other, false);
    });
    if (!menu.querySelector(':scope > details')?.open) {
      menu.cascadeHoverOpened = true;
      setMenuOpen(menu, true);
    }
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse' || !hoverMenus.matches) return;
    const menu = event.target.closest('header-menu');
    if (!menu || menu.contains(event.relatedTarget)) return;
    clearTimeout(menu.cascadeCloseTimer);
    menu.cascadeCloseTimer = setTimeout(() => {
      menu.cascadeHoverOpened = false;
      setMenuOpen(menu, false);
    }, HOVER_CLOSE_DELAY);
  });

  // Without this, clicking a menu that hover just opened would toggle it shut.
  // event.detail is 0 for keyboard (Enter/Space) clicks, which keep toggling.
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('header-menu > details > summary');
    if (!summary || event.detail === 0) return;
    const menu = summary.closest('header-menu');
    if (!menu.cascadeHoverOpened) return;
    event.preventDefault();
    menu.cascadeHoverOpened = false;
  });
})();
